import React, { useState, useEffect } from "react";
import { Box, Collapse, HStack, Icon, IconButton, Text, VStack } from "@chakra-ui/react";
import { FaChevronDown, FaChevronUp, FaSearch, FaTimes } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AnalysisOutputProto } from "build-ts/chat_session";
import { epochConverterWithTimeZone } from "utils/dateUtils";

export interface AnalysisOutputEntry {
  output: AnalysisOutputProto;
  onRemove: () => void;
}

interface AnalysisOutputCardProps {
  entries: AnalysisOutputEntry[];
  defaultExpanded?: boolean;
  collapseSignal?: number;
  expandSignal?: number;
  defaultTimeZone?: string;
  selectedRunIndex: number;
  onSelectedRunIndexChange: (index: number) => void;
}


const AnalysisOutputCard: React.FC<AnalysisOutputCardProps> = ({
  entries,
  defaultExpanded = true,
  collapseSignal,
  expandSignal,
  defaultTimeZone,
  selectedRunIndex,
  onSelectedRunIndexChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  // Use `!== undefined && !== null` instead of truthiness so signal value 0 fires correctly.
  useEffect(() => {
    if (collapseSignal !== undefined && collapseSignal !== null) setIsExpanded(false);
  }, [collapseSignal]);

  useEffect(() => {
    if (expandSignal !== undefined && expandSignal !== null) setIsExpanded(true);
  }, [expandSignal]);

  const active = entries[selectedRunIndex];
  const activeOutput = active?.output;
  const activeFindings = activeOutput?.findings?.trim() || "";

  const handleRemoveAll = () => entries.forEach((e) => e.onRemove());

  return (
    <Box width="100%" bg="white" overflow="hidden" display="flex" flexDirection="column" h="100%">
      {/* ── Card Header ──────────────────────────────────────── */}
      <HStack
        px={2}
        py={1}
        bg="gray.50"
        cursor="pointer"
        onClick={() => setIsExpanded((prev) => !prev)}
        justifyContent="space-between"
        borderBottom="1px solid"
        borderTop="1px solid"
        borderColor="gray.900"
        transition="background 0.15s"
        _hover={{ bg: "gray.100" }}
      >
        <HStack spacing={2} flex={1} minW={0}>
          <Icon as={FaSearch} boxSize={5} color="purple.600" flexShrink={0} />
          <Text fontSize="sm" fontWeight="600" color="gray.800" isTruncated mb={0}>
            Searched past runs
          </Text>
          <Text
            fontSize="xs"
            color="purple.700"
            bg="purple.50"
            px={2}
            py={0.5}
            borderRadius="sm"
            fontWeight="700"
            flexShrink={0}
            mb={0}
            border="1px solid"
            borderColor="purple.200"
          >
            {entries.length} result{entries.length !== 1 ? "s" : ""}
          </Text>
        </HStack>
        <IconButton
          size="xs"
          variant="ghost"
          icon={<Icon as={FaTimes} boxSize={4} />}
          color="red.400"
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveAll();
          }}
          aria-label="Close"
        />
        <Icon as={isExpanded ? FaChevronUp : FaChevronDown} boxSize={3} color="gray.600" mr={1} />
      </HStack>

      {/* ── Expanded Body ────────────────────────────────────── */}
      <Collapse in={isExpanded} animateOpacity style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <HStack spacing={0} align="stretch" flex={1} overflow="hidden">
          {/* ── Left — created time / date from Darwin header ── */}
          <VStack
            width="auto"
            flexShrink={0}
            align="stretch"
            spacing={0}
            overflowY="auto"
            borderRight="1px solid"
            borderColor="gray.200"
            sx={{
              scrollbarWidth: "thin",
              scrollbarColor: "#CBD5E0 transparent",
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "3px" },
            }}
            textAlign="left"
          >
            {entries.map((entry, idx) => {
              const created = entry.output.header?.created_date;
              const timeDayLine = created !== undefined ? epochConverterWithTimeZone(created, "h:mm a", defaultTimeZone ?? "") : "—";
              const dateLine = created !== undefined ? epochConverterWithTimeZone(created, "d MMM yyyy", defaultTimeZone ?? "") : "";
              const isActive = idx === selectedRunIndex;
              return (
                <Box
                  key={`${entry.output.header?.id ?? "row"}-${idx}`}
                  px={3}
                  py={2}
                  cursor="pointer"
                  borderBottom="1px solid"
                  borderColor="gray.500"
                  bg={isActive ? "purple.50" : "transparent"}
                  borderLeft="3px solid"
                  borderLeftColor={isActive ? "purple.500" : "transparent"}
                  _hover={{ bg: isActive ? "purple.50" : "gray.100" }}
                  transition="background 0.12s"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectedRunIndexChange(idx);
                  }}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    color={isActive ? "purple.800" : "gray.800"}
                    lineHeight="1.25"
                    mb={0}
                    textAlign="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {timeDayLine}
                  </Text>
                  {dateLine ? (
                    <Text
                      fontSize="xs"
                      fontWeight="500"
                      color="gray.600"
                      lineHeight="1.25"
                      mt={0.5}
                      mb={0}
                      textAlign="right"
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {dateLine}
                    </Text>
                  ) : null}
                </Box>
              );
            })}
          </VStack>

          {/* ── Right — findings only ── */}
          {activeOutput ? (
            <Box
              flex={1}
              minW={0}
              overflowY="auto"
              bg="white"
              px={2}
              py={1.5}
              textAlign="left"
              fontSize="sm"
              color="gray.700"
              fontWeight="500"
              sx={{
                scrollbarWidth: "thin",
                scrollbarColor: "#CBD5E0 transparent",
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "3px" },
                wordBreak: "break-word",
                "& p": { fontSize: "sm", marginTop: "0.35em", marginBottom: "0.35em", lineHeight: 1.65 },
                "& p:first-of-type": { marginTop: 0 },
                "& p:last-of-type": { marginBottom: 0 },
                "& ul, & ol": {
                  fontSize: "sm",
                  paddingLeft: "1.1rem",
                  marginTop: "0.35em",
                  marginBottom: "0.35em",
                },
                "& li": { fontSize: "sm", marginBottom: "0.2em", lineHeight: 1.6 },
                "& h1, & h2, & h3, & h4": {
                  fontSize: "sm",
                  fontWeight: 700,
                  marginTop: "0.6em",
                  marginBottom: "0.35em",
                  lineHeight: 1.35,
                },
                "& h1:first-child, & h2:first-child, & h3:first-child, & h4:first-child": {
                  marginTop: 0,
                },
                "& code": {
                  fontSize: "sm",
                  padding: "0.1em 0.35em",
                  borderRadius: "3px",
                  bg: "gray.100",
                },
                "& pre": {
                  fontSize: "sm",
                  padding: "0.5rem",
                  borderRadius: "md",
                  overflowX: "auto",
                  bg: "gray.50",
                  borderWidth: "1px",
                  borderColor: "gray.200",
                  marginTop: "0.35em",
                  marginBottom: "0.35em",
                },
                "& pre code": { padding: 0, bg: "transparent" },
                "& blockquote": {
                  borderLeftWidth: "3px",
                  borderLeftColor: "gray.300",
                  paddingLeft: "0.6rem",
                  marginY: "0.35em",
                  color: "gray.600",
                  fontSize: "sm",
                },
                "& table": {
                  fontSize: "sm",
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: "0.35em",
                  marginBottom: "0.35em",
                },
                "& th, & td": { borderWidth: "1px", borderColor: "gray.200", padding: "0.25rem 0.4rem" },
                "& th": { bg: "gray.50", fontWeight: 600 },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {activeFindings || "*No findings recorded.*"}
              </ReactMarkdown>
            </Box>
          ) : (
            <Box flex={1} display="flex" alignItems="center" justifyContent="center" px={2}>
              <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
                Select a run to view its findings.
              </Text>
            </Box>
          )}
        </HStack>
      </Collapse>
    </Box>
  );
};

export default AnalysisOutputCard;
